/**
 * Slot allocation UI helper
 */

/**
 * import utility functions
 */
import { constVal } from '../slot-const';
const findDefaultProfileName = (rtspProfiles = []) => {
  let defaultProfile = rtspProfiles.find(val => (val.codec === 'H264' || val.codec === 'H.264'));
  if (!defaultProfile) {
    defaultProfile = rtspProfiles.find(val => (val.codec === 'H265' || val.codec === 'H.265'));
  }
  return (defaultProfile && defaultProfile.profileName) || '';
};

const isCodecNotSupported = (encoding = '') => {
  return !(encoding === 'H264' || encoding === 'H.264' || encoding === 'H265' || encoding === 'H.265');
};

export const getSlotUILogic = (isDisabled) => {
  return {
    testedStatus: constVal.INITIAL,
    cameraListIdx: constVal.INVALID_INDEX,
    isDisabled,
    ipAddr: '',
    port: '',
    macAddr: '',
    comment: ''
  };
};

export const mapSlotAndCameraUILogic = (cameras = {}, slotCofig = {}) => {
  const { list: cameraList = [] } = cameras;
  const { nb_max_slot = 0, slots: slotList = [], nb_activ_slot = 0 } = slotCofig;
  const slotLen = slotList.length;
  let sortedCameraList = [];
  let sortedCameraListLen = 0;
  const mapCameraList = [];
  const mapSlotsList = [];

  // eslint-disable-next-line camelcase
  if (!nb_max_slot || !nb_activ_slot || nb_activ_slot > nb_max_slot) {
    return null;
  }

  // eslint-disable-next-line camelcase
  for (let i = 0; i < nb_max_slot; i++) {
    // eslint-disable-next-line camelcase
    mapSlotsList.push({ ui: getSlotUILogic(i >= nb_activ_slot) });
    if (i < slotLen) {
      const slot = slotList[i] || {};
      const testedStatus = slot.cameraTestedStatus || constVal.INITIAL;
      const macAddr = slot.cameraMAC || '';
      const comment = slot.cameraComment || '';
      mapSlotsList[i] = { ...slotList[i], ui: { ...mapSlotsList[i].ui, testedStatus, macAddr, comment } };
    }
  }

  // Sort the camera list(IPV4) in ascending order. Conside all the four octetes.
  sortedCameraList = cameraList.sort(({ ip: ipAddr2 = '' }, { ip: ipAddr1 = '' }) => {
    const address2 = ipAddr2.split('.');
    const address1 = ipAddr1.split('.');
    const len = address2.length;

    for (let i = 0; i < len; i++) {
      const portion2 = address2[i];
      const portion1 = address1[i];
      const diff = portion2 - portion1;
      if (diff) {
        return diff;
      }
    }
    return 0;
  });

  sortedCameraListLen = sortedCameraList.length;

  for (let i = 0; i < sortedCameraListLen; i++) {
    const camera = sortedCameraList[i];
    if (camera.macAddr && camera.ip && camera.port) {
      mapCameraList.push({ ...camera, ipAddr: camera.ip, ui: { isAssigned: false, assignedCount: 0 } });
      for (let j = 0; j < slotLen; j++) {
        const { ip:ipAddr = '', port = '' } = camera;
        if (camera.macAddr === slotList[j].cameraMAC) {
          mapCameraList[i].ui.isAssigned = true;
          mapCameraList[i].ui.assignedCount += 1;
          mapSlotsList[j] = { ...mapSlotsList[j], ui: { ...mapSlotsList[j].ui, ipAddr, port, cameraListIdx: i } };
        }
      }
    }
  }

  return { mapCameras: { ...cameras, list: mapCameraList }, mapSlotConfig: { ...slotCofig, slots: mapSlotsList } };
};

const mapProfileData = (profiles = []) => {
  return profiles.map((profile) => {
    const { video_config = {}, video_encoding = {} } = profile;
    return {
      profileName: profile.name,
      rtspUrl: profile.stream_uri || '',
      codec: video_encoding.encoding || '',
      width: video_encoding.resol_width,
      height: video_encoding.resol_height || '',
      fps: video_config.frame_rate || ''
    };
  });
};

export const mapTestedData = (data, macAddr, ipAddr) => {
  const { device_information: dm = {}, stream_uri = '', userName = '', password = '', comment = '', testedStatus, profiles = [] } = data || {};
  const rtspProfiles = mapProfileData(profiles);
  const defaultProfileName = findDefaultProfileName(rtspProfiles);
  return {
    cameraBrand: dm.manufacturer || '',
    cameraModel: dm.model || '',
    cameraSN: dm.serial_number || '',
    cameraMAC: macAddr,
    cameraHWid: dm.hardware_id || '',
    cameraMainRtsp: stream_uri,
    cameraComment: comment,
    cameraFWVersion: dm.firmware_version || '',
    cameraUserName: userName,
    cameraPassword: password,
    cameraTestedStatus: testedStatus,
    cameraIpAddr: ipAddr,
    cameraRtspChannels: rtspProfiles,
    cameraNvrRecRtspPreferred: defaultProfileName,
    cameraAnalyzeRtspPreferred: defaultProfileName
  };
};

const filterRtspByValidUri = (rtspChannels = []) => {
  return rtspChannels.filter(channel => channel.rtspUrl);
};

const addDisabledByCodec = (rtspChannel = {}) => {
  return {
    ...rtspChannel,
    disabled: isCodecNotSupported(rtspChannel.codec)
  };
};

export const getCameraExtendedInfo = (data, nvrSel, aiSel) => {
  // eslint-disable-next-line no-unused-vars
  const { ui = {}, cameraUserName, cameraPassword, ...rest } = data || {};
  return {
    contentList: [
      { key: 'IP', val: `${ui.ipAddr} - ${ui.port}` },
      { key: 'UUID', val: rest.cameraUUID },
      { key: 'Mac', val: rest.cameraMAC },
      { key: 'Brand', val: rest.cameraBrand },
      { key: 'Model', val: rest.cameraModel },
      { key: 'Serial Number', val: rest.cameraSN },
      { key: 'HW Id', val: rest.cameraHWid },
      { key: 'Label', val: rest.cameraComment },
      { key: 'FW Version', val: rest.cameraFWVersion },
      { key: 'Profile EWC/Liveview/AI', val: aiSel },
      { key: 'Profile NVR', val: nvrSel }
    ],
    profiles: filterRtspByValidUri(rest.cameraRtspChannels).map(addDisabledByCodec)
  };
};

export const getCameraNvrRtspPref = (data) => {
  return data.cameraNvrRecRtspPreferred || '';
};

export const getCameraAIRtspPref = (data) => {
  return data.cameraAnalyzeRtspPreferred || '';
};

export const getMsgDialogInitialState = () => {
  return { open: false };
};

export const formatTooltipProps = (props = {}) => {
  const { manuf = '', hwmodel = '' } = props;

  const profilesToString = () => {
    const profiles = Array.isArray(props.profiles) ? props.profiles : [];
    return profiles.reduce((acc, profile) => (acc ? `${acc}, ${profile}` : `${profile}`), '');
  };

  return [
    `Manufacturer: ${decodeURI(manuf)}`,
    `HW Model: ${hwmodel}`,
    `Profiles: ${profilesToString()}`
  ];
};

// eslint-disable-next-line no-unused-vars
export const confDetailsJsonToObj = (config_details='') => {
  return JSON.parse(config_details);
};

// export const confDetailsJsonToObj = (config_details='') => {
//   let obj = JSON.parse(config_details);
//   obj.basic_installation = {
//     install_progress_step: 4,
//     camera_discovered: {
//       camerasTable: Array(15).fill(null).map((_, idx) => {
//         return {
//           ip: `10.27.15.${idx}`,
//           port: 80,
//           macAddr: `12:45:78:34:56:${idx}`
//         };
//       })
//     },
//   };
//   obj.slot_configuration = {
//     nb_activ_slot: 8,
//     nb_max_slot: 12,
//     slots: [
//       {
//         cameraBrand: 'Axis',
//         cameraIpAddr: '10.27.15.1',
//         cameraTestedStatus: 2,
//         cameraMAC: '12:45:78:34:56:1',
//         port: 80,
//         cameraUserName: 'admin',
//         cameraPassword: 'admin',
//         cameraAnalyzeRtspPreferred: '',
//         cameraNvrRecRtspPreferred: '',
//         cameraRtspChannels: [{
//           profileName: 'S',
//           codec: 'H264',
//           fps: 30,
//           height: 720,
//           width: 1280,
//           rtspUrl: 'rtsp://10.27.15.130/onvig-media/S'
//         }, {
//           profileName: 'D',
//           codec: 'H264',
//           fps: 30,
//           height: 720,
//           width: 1280,
//           rtspUrl: 'rtsp://10.27.15.130/onvig-media/D'
//         }, {
//           profileName: 'E',
//           codec: 'JPEG',
//           fps: 30,
//           height: 720,
//           width: 1280,
//           rtspUrl: 'rtsp://10.27.15.130/onvig-media/E'
//         }, {
//           profileName: 'U',
//           codec: 'H264',
//           fps: 30,
//           height: 720,
//           width: 1280
//         }]
//       }
//     ]
//   };
//   return obj;
// };

// const mapProfileData = (profiles = []) => {
//   return Array(2).fill(null).map((_, idx) => {
//     if (idx === 1) {
//       return {
//         profileName: `profile_${idx + 1}`,
//         rtspUrl: 'afksdjflskdaflsadjfisajflksa',
//         codec: 'H265',
//         resolution: '720',
//         fps: '26'
//       };
//     } else {
//       return {
//         profileName: `profile_${idx + 1}`,
//         rtspUrl: 'akjfksajklsajdfkl',
//         codec: 'H264',
//         resolution: '720',
//         fps: '26'
//       };
//     }
//   });
// };
